/**
 * Conditions on objects: the variables and their types are declared per object (e.g.
 * functions/orders/orderConditions.ts), the value of a variable comes from getObjectConditionValue (e.g.
 * getOrderConditionValue in functions/orders/orders.ts) and is checked here.
 */

export const CONDITION_OPERATORS = ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'contains', 'ncontains', 'in', 'any', 'none'] as const

export type ConditionOperator = typeof CONDITION_OPERATORS[number]

export type ConditionValue = string | number | boolean | string[]

/** The value of a variable of an object, a list holds one item per stop, collo or option */
export type ObjectConditionValue = string | number | boolean | undefined | (string | string[] | undefined)[]

// The fields are optional like the inferred models (server/models/conditions.ts), a condition without them never matches
export interface Condition<V extends string = string> {
    variable?: V
    operator?: ConditionOperator
    value?: ConditionValue
}

/** The operators per type of variable, in the order they are offered */
export const CONDITION_OPERATORS_BY_TYPE = {
    number: ['eq', 'neq', 'lt', 'lte', 'gt', 'gte'],
    time: ['lt', 'gt'],
    text: ['eq', 'neq', 'contains', 'ncontains', 'any', 'none'],
    list: ['in', 'contains', 'any', 'none'],
    choice: ['any', 'none'],
    postalCode: ['in', 'any', 'none'],
    boolean: ['eq']
} as const satisfies Record<string, readonly ConditionOperator[]>

/**
 * - number, time (HH:mm), boolean: one value to compare with
 * - text: one text, or a list of texts for any/none
 * - list: the value has several items (colli types, options), choice: one item (customer, country), both with a
 *   list of chosen values
 * - postalCode: ranges or prefixes (e.g. "3500-3599, 1012")
 */
export type ConditionType = keyof typeof CONDITION_OPERATORS_BY_TYPE

/** The variables of an object, as a non-empty list for a zod enum */
export function conditionVariables<V extends string>(types: Record<V, ConditionType>): [V, ...V[]] {
    return Object.keys(types) as [V, ...V[]]
}

/** The value of a new condition, or of a condition whose variable or operator changes from `value` */
export function conditionValue(type: ConditionType, operator: ConditionOperator, value?: ConditionValue): ConditionValue {
    const text = Array.isArray(value) ? value[0] || '' : typeof value === 'string' ? value : ''

    switch (type) {
        case 'list':
        case 'choice':
            return Array.isArray(value) ? value : text ? [text] : []
        case 'text':
            return operator === 'any' || operator === 'none' ? (Array.isArray(value) ? value : text ? [text] : []) : text
        case 'time':
        case 'postalCode':
            return text
        case 'boolean':
            return typeof value === 'boolean' ? value : true
        default:
            return typeof value === 'number' ? value : 0
    }
}

/** Whether an item of the value (e.g. a colli type, or the zones of one stop) matches a chosen condition value */
export type ConditionMatcher = (item: string | string[], conditionValue: string) => boolean

/** The values of one object (e.g. an order) for conditions, see conditionValues */
export interface ConditionValues<V extends string> {
    get: (variable: V) => ObjectConditionValue
    /** How an item of the value of a variable matches a chosen value, equal by default */
    matchers: {[variable in V]?: ConditionMatcher}
}

/**
 * The values of an object for conditions. Each value is computed once, so the object can be checked against many
 * condition groups (e.g. all automatic fees). Create it again when the object changes.
 */
export function conditionValues<V extends string>(getValue: (variable: V) => ObjectConditionValue, matchers: ConditionValues<V>['matchers'] = {}): ConditionValues<V> {
    const values = new Map<V, ObjectConditionValue>()

    return {
        get: (variable) => {
            if (!values.has(variable)) {
                values.set(variable, getValue(variable))
            }

            return values.get(variable)
        },
        matchers
    }
}

/** Text is compared without capitals and surrounding spaces, a time (HH:mm) as minutes */
function comparable(value: unknown): string | number | boolean | undefined {
    if (typeof value === 'string') {
        const time = /^(\d{1,2}):(\d{2})$/.exec(value.trim())

        if (time) {
            return parseInt(time[1]) * 60 + parseInt(time[2])
        }

        return value.trim().toLowerCase()
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return value
    }

    return undefined
}

/** Text for Bevat and Bevat niet, also when it looks like a time */
function comparableText(value: unknown): string | undefined {
    return typeof value === 'string' ? value.trim().toLowerCase() : undefined
}

/** A number stored as text (e.g. "1000" through the API) is compared with a number as a number */
function asNumber(value: string | number | boolean, other: string | number | boolean): string | number | boolean {
    if (typeof value === 'string' && typeof other === 'number' && /^-?\d+([.,]\d+)?$/.test(value)) {
        return Number(value.replace(',', '.'))
    }

    return value
}

const equals: ConditionMatcher = (item, conditionValue) => {
    if (Array.isArray(item)) {
        return item.some((value) => comparable(value) === comparable(conditionValue))
    }

    return comparable(item) === comparable(conditionValue)
}

function compare(value: unknown, operator: ConditionOperator, conditionValue: unknown): boolean {
    let a = comparable(value)
    let b = comparable(conditionValue)

    if (a === undefined || b === undefined) {
        return operator === 'neq' && a !== b
    }

    a = asNumber(a, b)
    b = asNumber(b, a)

    switch (operator) {
        case 'eq':
            return a === b
        case 'neq':
            return a !== b
        case 'lt':
            return typeof a === typeof b && a < b
        case 'lte':
            return typeof a === typeof b && a <= b
        case 'gt':
            return typeof a === typeof b && a > b
        case 'gte':
            return typeof a === typeof b && a >= b
        default:
            return false
    }
}

/**
 * Whether the value of a variable matches a condition.
 * - eq, neq, lt, lte, gt, gte: compare a number, time, text or yes/no. On a list of texts eq matches when one of
 *   them is equal, neq when none of them is.
 * - contains, ncontains on text: the condition text is (not) part of the text, on a list of texts of one of them (none of them).
 * - in: every item of the value is one of the condition values (Alle in)
 * - contains with a list of condition values: every condition value is in the value (Bevat alle)
 * - any: an item of the value is one of the condition values (Een van), none: no item is (Geen van)
 * A single value counts as a list of one item.
 */
function checkCondition(value: ObjectConditionValue, operator: ConditionOperator, conditionValue: ConditionValue, matches: ConditionMatcher = equals): boolean {
    if (value === undefined || value === null) {
        return false
    }

    const items = (Array.isArray(value) ? value : [value]).filter((item) => item !== undefined && item !== null).map((item) => {
        return Array.isArray(item) ? item : String(item)
    })

    const conditionValues = (Array.isArray(conditionValue) ? conditionValue : [conditionValue]).map(String).filter((item) => item.trim() !== '')

    const isChosen = (item: string | string[]) => conditionValues.some((chosen) => matches(item, chosen))

    switch (operator) {
        case 'eq':
            return Array.isArray(value) ? value.some((item) => compare(item, operator, conditionValue)) : compare(value, operator, conditionValue)
        case 'neq':
            return Array.isArray(value) ? value.every((item) => compare(item, operator, conditionValue)) : compare(value, operator, conditionValue)
        case 'lt':
        case 'lte':
        case 'gt':
        case 'gte':
            return !Array.isArray(value) && compare(value, operator, conditionValue)
        case 'contains':
        case 'ncontains': {
            if (Array.isArray(conditionValue)) {
                const containsAll = conditionValues.every((chosen) => items.some((item) => matches(item, chosen)))
                return operator === 'contains' ? containsAll : !containsAll
            }

            const text = comparableText(conditionValue)

            if (!text) {
                return false
            }

            const containsText = items.some((item) => !Array.isArray(item) && !!comparableText(item)?.includes(text))

            return operator === 'contains' ? containsText : !containsText
        }
        case 'in':
            return items.every(isChosen)
        case 'any':
            return items.some(isChosen)
        case 'none':
            return !items.some(isChosen)
        default:
            return false
    }
}

/** Whether the values of an object match a condition */
function matchesCondition<V extends string>(values: ConditionValues<V>, condition: Condition<V>): boolean {
    if (!condition.variable || !condition.operator) {
        return false
    }

    return checkCondition(values.get(condition.variable), condition.operator, condition.value, values.matchers[condition.variable])
}

/**
 * Whether condition groups match. With matchAll (En) all conditions within a group must match and one of the groups,
 * otherwise (Of) one condition within each group must match. Without conditions everything matches.
 */
function matchesConditionGroups<C>(conditionGroups: C[][] | undefined, matchAll: boolean, check: (condition: C) => boolean): boolean {
    const groups = (conditionGroups || []).filter((group) => group.length)

    if (!groups.length) {
        return true
    }

    return matchAll ?
        groups.some((group) => group.every(check)) :
        groups.every((group) => group.some(check))
}

/** Whether the values of an object match condition groups, see matchesConditionGroups */
function matchesConditions<V extends string>(values: ConditionValues<V>, conditionGroups: Condition<V>[][] | undefined, matchAll: boolean): boolean {
    return matchesConditionGroups(conditionGroups, matchAll, (condition) => matchesCondition(values, condition))
}

export {checkCondition, matchesCondition, matchesConditionGroups, matchesConditions}

export default {
    conditionValues,
    checkCondition,
    matchesCondition,
    matchesConditionGroups,
    matchesConditions
}
